import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// Fix for missing marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

export type MapPoint = {
  latitude: number;
  longitude: number;
  label?: string;
};

type Props = {
  points: MapPoint[];
  height?: string | number;
  zoom?: number;
};

function FitBounds({ points, zoom }: { points: MapPoint[]; zoom?: number }) {
  const map = useMap();

  // Fix: Leaflet doesn't know container size when inside tabs/modals/hidden containers.
  // invalidateSize() forces Leaflet to recalculate the container dimensions and load tiles.
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 150);

    if (!points.length) return () => clearTimeout(timer);

    if (points.length > 1) {
      const bounds = L.latLngBounds(points.map((p) => [p.latitude, p.longitude]));
      map.fitBounds(bounds, { padding: [50, 50] });
    } else if (points.length === 1) {
      map.setView([points[0].latitude, points[0].longitude], zoom ?? 12);
    }

    if (zoom) {
      map.setZoom(zoom);
    }

    return () => clearTimeout(timer);
  }, [points, zoom, map]);

  // Also invalidate on every re-render (handles tab switches, accordion opens, etc.)
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 350);
    return () => clearTimeout(t);
  });

  return null;
}

const LocationMap: React.FC<Props> = ({ points, height = "300px", zoom }) => {
  if (!points.length) return null;

  return (
    <MapContainer
      center={[points[0].latitude, points[0].longitude]}
      zoom={zoom ?? 12}
      style={{ height, width: "100%", minHeight: "200px" }}
      scrollWheelZoom={false}
    >
      <TileLayer
        attribution="&copy; OpenStreetMap contributors"
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {points.map((p, idx) => (
        <Marker key={idx} position={[p.latitude, p.longitude]}>
          <Popup>{p.label || `Point ${idx + 1}`}</Popup>
        </Marker>
      ))}
      <FitBounds points={points} zoom={zoom} />
    </MapContainer>
  );
};

export default LocationMap;